import { useQuery } from '@tanstack/react-query'
import ImageGalleryLightbox from 'LuxKit/Carousel/ImageGalleryLightbox'
import { useMemo } from 'react'
import { getPlaceInfo } from 'tripPlanner/api'
import * as PlaceInfoKeys from 'tripPlanner/hooks/reactQueryKeys/placeInfo'
import { mapPlaceImageIds, type PlaceImageWithId } from 'tripPlanner/utils/placeImages'

export interface PlaceInfoGalleryLightboxProps {
  googlePlaceId: string
  images: Array<PlaceImageWithId>
  failedImageIds: Array<string>
  defaultIndex: number
}

function getSafeGalleryIndex(images: Array<PlaceImageWithId>, requestedIndex: number): number {
  return Math.min(requestedIndex, Math.max(images.length - 1, 0))
}

function PlaceInfoGalleryLightbox({
  googlePlaceId,
  images,
  failedImageIds,
  defaultIndex,
}: PlaceInfoGalleryLightboxProps) {
  const { data: galleryPlaceInfo } = useQuery({
    queryKey: PlaceInfoKeys.info(googlePlaceId, 'gallery'),
    queryFn: () => getPlaceInfo(googlePlaceId, undefined, 'gallery'),
    staleTime: Infinity,
  })

  const galleryImages = useMemo(() => {
    return mapPlaceImageIds(galleryPlaceInfo?.imageIds)
      .filter((image) => !failedImageIds.includes(image.id))
  }, [failedImageIds, galleryPlaceInfo?.imageIds])

  const lightboxImages = galleryImages.length > 0 ? galleryImages : images

  return (
    <ImageGalleryLightbox
      images={lightboxImages}
      defaultIndex={getSafeGalleryIndex(lightboxImages, defaultIndex)}
    />
  )
}

export default PlaceInfoGalleryLightbox
